import {
  GoogleAuthProvider,
  getAuth,
  getRedirectResult,
  signInWithPopup,
  signInWithRedirect,
  signOut,
} from "firebase/auth";
import { ApolloError } from "@apollo/client";
import { logToServer } from "../shared/utils";
import { LOGIN_GOOGLE_USER_QUERY } from "../shared/queries";

const SIGNING_IN_KEY = "isSigningIn";
export const REDIRECTING_TO_SIGN_IN = "Redirecting to Google sign-in";

/** Redirect needs same-origin HTTPS authDomain; HTTP localhost cannot provide that. */
function canUseRedirectAuth(): boolean {
  return window.location.protocol === "https:";
}

export type StoredUserProfile = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  imageUrl: string;
};

export function getStoredUserProfile(): StoredUserProfile | null {
  const raw = localStorage.getItem("userProfile");
  if (!raw) return null;
  try {
    return JSON.parse(raw) as StoredUserProfile;
  } catch {
    return null;
  }
}

export function getDisplayName(profile: StoredUserProfile | null): string {
  if (!profile) return "Account";
  const name = [profile.firstName, profile.lastName].filter(Boolean).join(" ");
  return name || profile.email || "Account";
}

export async function loginWithBackend(
  client: any,
  email: string | null,
  googleId: string,
  googleToken: string | null
) {
  if (!email || !googleToken) {
    throw new Error("Missing required credentials");
  }

  const queryResult = await client.query({
    query: LOGIN_GOOGLE_USER_QUERY,
    variables: {
      email,
      googleId,
      googleToken,
    },
    fetchPolicy: "network-only",
  });

  logToServer("loginGoogleUser", queryResult);
  const user = queryResult.data.loginGoogleUser;
  localStorage.setItem("token", user.token);
  localStorage.setItem("user", user.id);
  const profile: StoredUserProfile = {
    id: user.id,
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    imageUrl: user.imageUrl,
  };
  localStorage.setItem("userProfile", JSON.stringify(profile));
  return user;
}

export async function logout() {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  localStorage.removeItem("userProfile");
  sessionStorage.removeItem(SIGNING_IN_KEY);
  try {
    await signOut(getAuth());
  } catch (error) {
    console.error("Firebase sign-out error:", error);
  }
  const currentUrl = new URL(window.location.href);
  currentUrl.searchParams.set("auth", "true");
  window.location.href = currentUrl.toString();
}

function createGoogleProvider() {
  const provider = new GoogleAuthProvider();
  provider.addScope("https://www.googleapis.com/auth/userinfo.email");
  provider.addScope("https://www.googleapis.com/auth/userinfo.profile");
  provider.setCustomParameters({
    access_type: "offline",
    prompt: "consent",
  });
  return provider;
}

function completeWithCredential(
  client: any,
  result: import("firebase/auth").UserCredential
) {
  const credential = GoogleAuthProvider.credentialFromResult(result);
  const token = credential?.accessToken ?? null;
  logToServer("token", { token });
  const user = result.user;
  logToServer("user", { user });
  return loginWithBackend(client, user.email, user.uid, token);
}

export function signInWithGoogle(client: any) {
  return new Promise((resolve, reject) => {
    const provider = createGoogleProvider();
    const auth = getAuth();

    if (!canUseRedirectAuth()) {
      // HTTP localhost: Firebase forces https://{authDomain}/__/auth/handler, so
      // same-origin redirect is impossible. Use popup for local HTTP only.
      sessionStorage.removeItem(SIGNING_IN_KEY);
      signInWithPopup(auth, provider)
        .then((result) => completeWithCredential(client, result))
        .then(resolve)
        .catch((error) => {
          logToServer("Error in login", {
            errorCode: error.code,
            errorMessage: error.message,
          });
          reject(error);
        });
      return;
    }

    getRedirectResult(auth)
      .then((result) => {
        if (result) {
          sessionStorage.removeItem(SIGNING_IN_KEY);
          return completeWithCredential(client, result);
        }

        // Returned from a redirect with no credential — stop to avoid a loop.
        if (sessionStorage.getItem(SIGNING_IN_KEY)) {
          sessionStorage.removeItem(SIGNING_IN_KEY);
          throw new Error("Google sign-in did not complete. Please try again.");
        }

        sessionStorage.setItem(SIGNING_IN_KEY, "1");
        signInWithRedirect(auth, provider);
        throw new Error(REDIRECTING_TO_SIGN_IN);
      })
      .then((userData) => {
        resolve(userData);
      })
      .catch((error) => {
        logToServer("Error in login", {
          errorCode: error.code,
          errorMessage: error.message,
        });
        reject(error);
      });
  });
}

export function checkAuthError(error: ApolloError) {
  if (
    error.networkError &&
    "statusCode" in error.networkError &&
    error.networkError.statusCode === 401
  ) {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set("auth", "true");
    window.location.href = currentUrl.toString();
  }
}
